import { useSyncExternalStore } from 'react';
import axios from 'axios';
import GeoJSONLayer from '@arcgis/core/layers/GeoJSONLayer';
import Graphic from '@arcgis/core/Graphic';
import Point from '@arcgis/core/geometry/Point';
import SimpleRenderer from '@arcgis/core/renderers/SimpleRenderer';
import PopupTemplate from '@arcgis/core/PopupTemplate';
import type { FeatureCollection, Point as GeoJsonPoint } from 'geojson';
import config from 'constants/apiConfig';
import {
  feedDisplayName,
  type ExternalFeedField,
  type FeedUnits,
} from 'components/MapPage/CorporateViewTabs/EventsTab/externalFeeds';

/** `GET /external_layers/:id/feed-features` and `POST /:id/refresh`. */
export type FeedFeaturesResponse = {
  geojson: FeatureCollection<GeoJsonPoint>;
  fetchedAt: string;
  units: FeedUnits;
  primaryField: string;
  fields: ExternalFeedField[];
  assetCount: number;
  skippedAssets: number;
  failedAssets: number;
};

export type FeedLayerStatus = {
  fetchedAt: string | null;
  units: FeedUnits | null;
  refreshing: boolean;
  error: string | null;
};

const OBJECT_ID_FIELD = 'ObjectID';
const EMPTY_STATUS: FeedLayerStatus = {
  fetchedAt: null,
  units: null,
  refreshing: false,
  error: null,
};

const mapLayers = new Map<string, GeoJSONLayer>();
const statuses = new Map<string, FeedLayerStatus>();
const listeners = new Set<() => void>();

function setStatus(layerId: string, patch: Partial<FeedLayerStatus>) {
  statuses.set(layerId, {
    ...(statuses.get(layerId) ?? EMPTY_STATUS),
    ...patch,
  });
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Last fetch time, units and refresh state of a feed layer, for its list item. */
export function useFeedLayerStatus(layerId: string): FeedLayerStatus {
  return useSyncExternalStore(
    subscribe,
    () => statuses.get(layerId) ?? EMPTY_STATUS
  );
}

function errorMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const message = (err.response?.data as { message?: string } | undefined)
      ?.message;
    if (message) return message;
  }
  return err instanceof Error ? err.message : 'Data feed request failed.';
}

export async function fetchFeedFeatures(
  layerId: string,
  options: { fresh?: boolean } = {}
): Promise<FeedFeaturesResponse> {
  const base = `${config.apiGateway.external_layers}/${layerId}`;
  const resp = options.fresh
    ? await axios.post<{ data: FeedFeaturesResponse }>(`${base}/refresh`)
    : await axios.get<{ data: FeedFeaturesResponse }>(`${base}/feed-features`);
  return resp.data.data;
}

function layerFields(fields: ExternalFeedField[]): __esri.FieldProperties[] {
  const seen = new Set<string>([OBJECT_ID_FIELD]);
  const out: __esri.FieldProperties[] = [
    { name: OBJECT_ID_FIELD, alias: 'Object ID', type: 'oid' },
  ];
  for (const field of [
    ...fields,
    {
      name: 'resource_set_id',
      alias: 'Asset ID',
      type: 'esriFieldTypeString' as const,
    },
  ]) {
    if (seen.has(field.name)) continue;
    seen.add(field.name);
    out.push({
      name: field.name,
      alias: field.alias,
      // Dates arrive as ISO strings; strings keep them readable in the popup.
      type: field.type === 'esriFieldTypeDouble' ? 'double' : 'string',
    });
  }
  return out;
}

/** Lucide `cloud-sun` (0.556), drawn on a 24-unit grid. */
const CLOUD_SUN_PATHS = [
  'M12 2v2',
  'm4.93 4.93 1.41 1.41',
  'M20 12h2',
  'm19.07 4.93-1.41 1.41',
  'M15.947 12.65a4 4 0 0 0-5.925-4.128',
  'M13 22H7a5 5 0 1 1 4.9-6H13a3 3 0 0 1 0 6Z',
];
const FORECAST_ICON_COLOR = 'rgb(14, 116, 178)';

const FORECAST_ICON_SVG = [
  '<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32">',
  `<circle cx="16" cy="16" r="13.5" fill="#ffffff" stroke="${FORECAST_ICON_COLOR}" stroke-width="2.5"/>`,
  `<g transform="translate(7 6.5) scale(0.75)" fill="none" stroke="${FORECAST_ICON_COLOR}" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">`,
  ...CLOUD_SUN_PATHS.map((d) => `<path d="${d}"/>`),
  '</g></svg>',
].join('');

export const FORECAST_ICON_URL = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(
  FORECAST_ICON_SVG
)}`;

/** The same forecast icon on every asset; the values live in the popup. */
export function feedRenderer(): __esri.SimpleRendererProperties {
  return {
    label: 'Forecast',
    symbol: {
      type: 'picture-marker',
      url: FORECAST_ICON_URL,
      width: '30px',
      height: '30px',
    },
  };
}

export function feedPopupTemplate(
  fields: ExternalFeedField[]
): __esri.PopupTemplateProperties {
  return {
    title: 'Forecast for {asset_name}',
    content: [
      {
        type: 'fields',
        fieldInfos: fields
          .filter((field) => field.name !== 'asset_name')
          .map((field) => ({ fieldName: field.name, label: field.alias })),
      },
    ],
  };
}

function withObjectIds(
  geojson: FeatureCollection<GeoJsonPoint>
): FeatureCollection<GeoJsonPoint> {
  return {
    ...geojson,
    features: geojson.features.map((feature, index) => ({
      ...feature,
      properties: { ...feature.properties, [OBJECT_ID_FIELD]: index + 1 },
    })),
  };
}

/** One forecast icon per asset. */
export async function createFeedLayer(externalLayer: {
  id: string;
  name?: string;
  serviceUrl?: string | null;
}): Promise<GeoJSONLayer> {
  const layerId = String(externalLayer.id);
  setStatus(layerId, { refreshing: true, error: null });
  try {
    const data = await fetchFeedFeatures(layerId);
    const blob = new Blob([JSON.stringify(withObjectIds(data.geojson))], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const layer = new GeoJSONLayer({
      id: `external-feed-${layerId}`,
      title:
        externalLayer.name ||
        feedDisplayName(externalLayer.serviceUrl) ||
        'Data feed',
      url,
      objectIdField: OBJECT_ID_FIELD,
      fields: layerFields(data.fields),
      outFields: ['*'],
      renderer: new SimpleRenderer(feedRenderer()),
      popupTemplate: new PopupTemplate(feedPopupTemplate(data.fields)),
    });
    try {
      await layer.load();
    } finally {
      URL.revokeObjectURL(url);
    }
    mapLayers.set(layerId, layer);
    setStatus(layerId, {
      fetchedAt: data.fetchedAt,
      units: data.units,
      refreshing: false,
    });
    return layer;
  } catch (err) {
    setStatus(layerId, { refreshing: false, error: errorMessage(err) });
    throw err;
  }
}

function toGraphics(geojson: FeatureCollection<GeoJsonPoint>): Graphic[] {
  return geojson.features.map(
    (feature) =>
      new Graphic({
        geometry: new Point({
          longitude: feature.geometry.coordinates[0],
          latitude: feature.geometry.coordinates[1],
        }),
        attributes: { ...feature.properties },
      })
  );
}

/**
 * Fetches a fresh forecast (skipping the server cache) and swaps the map
 * layer's points in place. Units come from the layer's saved `serviceUrl`.
 */
export async function refreshFeedLayer(layerId: string): Promise<void> {
  const id = String(layerId);
  setStatus(id, { refreshing: true, error: null });
  try {
    const data = await fetchFeedFeatures(id, { fresh: true });
    const layer = mapLayers.get(id);
    if (layer && !layer.destroyed) {
      const objectIds = await layer.queryObjectIds();
      await layer.applyEdits({
        deleteFeatures: objectIds.map((objectId) => ({ objectId })),
        addFeatures: toGraphics(data.geojson),
      });
      layer.popupTemplate = new PopupTemplate(feedPopupTemplate(data.fields));
    }
    setStatus(id, {
      fetchedAt: data.fetchedAt,
      units: data.units,
      refreshing: false,
    });
  } catch (err) {
    setStatus(id, { refreshing: false, error: errorMessage(err) });
    throw err;
  }
}
